import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * Reorder suggestions → a draft purchase order (the gap document's "reorder
 * builder" under Import order; PART 7 PUR-01's draft is the output).
 *
 * Permission area: `purchasing` (PART 16.2.2 row 2) — reading the suggestion
 * grid is `purchasing.view`, building the draft is `purchasing.create`: the
 * same two doors the PO list and the PO create route already stand behind,
 * because the build IS a PO create with its lines pre-filled.
 *
 * ── THE SUGGESTION FORMULA ─────────────────────────────────────────────────
 *
 *   available    = on_hand − reserved           (the stock layer's own definition)
 *   target       = maxLevel ?? reorderPoint     (the overstock ceiling when one is
 *                                                set — "fill to the top" — else
 *                                                back to the minimum)
 *   suggestedQty = max(reorderQty, target − available), clamped at ≥ 0
 *
 * `reorderQty` is the floor so a company that said "always buy a case of 24"
 * never gets a suggestion for 3; `target − available` is what the gap
 * actually is, and it grows past the floor when stock has fallen far — an item
 * whose `available` has gone NEGATIVE (a warehouse that allows negative stock)
 * is caught by exactly this term, since a negative available is below any
 * point and the gap counts the shortfall on both sides of zero. The figures
 * are the per-warehouse overrides (IT-S1, `item_warehouse_levels`) when a
 * warehouse is asked for and the row has one, else the item's own.
 *
 * ── "BELOW MINIMUM" ────────────────────────────────────────────────────────
 *
 * The same predicate INV-05 and the Items list call `low` (`LOW_STOCK` in
 * `stock-status.ts`): the point is > 0 AND available ≤ point. An item with no
 * reorder level never appears as low (§11 of DSH-03), so the default grid —
 * `belowMinimumOnly=true` — shows only items whose minimum is set and reached.
 * Switching it off lists every stocked item in scope, which is how a buyer
 * tops up a supplier's whole range in one order; `includeZero` then decides
 * whether rows the formula answers `0` for stay on screen.
 *
 * ── THE DEFAULT UNIT PRICE AT BUILD TIME ───────────────────────────────────
 *
 * A line that names no `unitPrice` is priced from the most recent POSTED bill
 * line for the item IN THE SUPPLIER'S CURRENCY (R9: drafts price nothing); if
 * no such bill exists and the supplier buys in the base currency, the item's
 * `cost_price` rounded to money's 2dp; otherwise `0.00`, which the draft PO
 * editor exists to correct. The grid's `lastCost` is the same lookup in the
 * BASE currency — an estimate for the footer, not a quote.
 */
// ── The fixed sentences of the reorder builder ──────────────────────────────
export const REORDER_MESSAGES = {
    /** The build door with nothing ticked — BR-SAL-00-15's shape. */
    noLines: 'Tick at least one line to build the order.',
    /** A service or an assembled-at-sale kit cannot be reordered into stock. */
    itemNotStocked: (code) => `${code} is not a stocked item and cannot be put on a reorder.`,
};
/**
 * D46 — the builder's one parameterised refusal, structured at birth. `code`
 * is the item's printed code, a display value, embedded verbatim in both
 * renders.
 */
export const REORDER_REFUSALS = {
    itemNotStocked: (code) => ({
        message: REORDER_MESSAGES.itemNotStocked(code),
        key: 'reorder.errItemNotStocked',
        params: { code },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ReorderSuggestionRowSchema = z
    .object({
    itemId: z.uuid(),
    itemCode: z.string(),
    name: z.string(),
    /** S5 / IT-C1: the Arabic twin; `localizedName()` picks it on an Arabic screen. */
    nameAr: z.string().nullable(),
    /** N-4: the STOCK unit every quantity on the row is measured in, and its decimals. */
    unit: z.string(),
    unitPrecision: UnitPrecisionSchema,
    barcode: z.string().nullable(),
    /** The warehouse the row was judged for; null = company-wide totals. */
    warehouseId: z.uuid().nullable(),
    warehouseName: z.string().nullable(),
    onHand: z.string(),
    reserved: z.string(),
    available: z.string(),
    reorderPoint: z.string(),
    reorderQty: z.string(),
    /** The overstock ceiling in force for the row; null = no ceiling. */
    maxLevel: z.string().nullable(),
    /** The formula's answer, ≥ 0, in the stock unit's decimals. */
    suggestedQty: z.string(),
    /** 2dp, in `currency`: the last posted base-currency bill price, else `cost_price`. */
    lastCost: z.string(),
    /** The supplier on that last posted bill, when one exists. */
    lastSupplierId: z.uuid().nullable(),
    lastSupplierName: z.string().nullable(),
    /** MD-03 panel 8: the item's own preferred supplier. */
    preferredSupplierId: z.uuid().nullable(),
    preferredSupplierName: z.string().nullable(),
    /** What `lastCost` is in — the company's base currency. */
    currency: z.string(),
})
    .strict();
export const ReorderSuggestionsQuerySchema = pagedQuery({
    warehouseId: z.uuid().optional(),
    categoryId: z.uuid().optional(),
    manufacturerId: z.uuid().optional(),
    /** Rows whose preferred OR last supplier is this partner. */
    supplierId: z.uuid().optional(),
    /** Default on: only items at or under a set minimum (the `low` ladder). */
    belowMinimumOnly: z.enum(['true', 'false']).default('true'),
    /** Default off: rows the formula answers `0` for are dropped. */
    includeZero: z.enum(['true', 'false']).default('false'),
    /** Item code, name (either language) or barcode. */
    q: z.string().trim().max(120).optional(),
});
export const ReorderLineInputSchema = z
    .object({
    itemId: z.uuid(),
    qty: QtyStringSchema,
    /** Omitted = the default-price rule in the header comment. */
    unitPrice: MoneyStringSchema.optional(),
})
    .strict();
export const BuildReorderRequestSchema = z
    .object({
    supplierId: z.uuid(),
    expectedDate: IsoDateSchema.optional(),
    /**
     * May arrive EMPTY: the no-lines wall is a refusal with its own sentence
     * (`REORDER_MESSAGES.noLines`), not a shape error, so an API caller reads
     * what to do rather than a parser's complaint.
     */
    lines: ReorderLineInputSchema.array().max(200),
})
    .strict();
export const BuildReorderResponseSchema = z
    .object({
    purchaseOrderId: z.uuid(),
    /** PO-#### — the draft's printed code. */
    code: z.string(),
})
    .strict();
export const reorderContract = c.router({
    suggestions: {
        method: 'GET',
        path: '/api/purchases/reorder/suggestions',
        query: ReorderSuggestionsQuerySchema,
        responses: {
            200: pagedResponse('rows', ReorderSuggestionRowSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Reorder suggestions (purchasing.view): stocked items with their levels, availability and the suggested quantity — filtered in SQL and paged (NFR-42).',
    },
    build: {
        method: 'POST',
        path: '/api/purchases/reorder/build',
        body: BuildReorderRequestSchema,
        responses: {
            201: BuildReorderResponseSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Build a DRAFT purchase order from reorder lines (purchasing.create) through the PUR-01 create path; prices default per the last-posted-bill rule.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=reorder.js.map